// InternalAuditReportTable.tsx
import React from 'react';

interface AuditRecord {
  category: string;
  totalNC: number | null;
  completionNC: number | null;
  pending: number | null;
  completionPercent: number | null;
  currentScore: number | null;
  maxScore: number;
}

interface InternalAuditReportTableProps {
  records?: AuditRecord[];
  onChange?: (index: number, updatedRecord: AuditRecord) => void;
  isEditable?: boolean;
}

export default function InternalAuditReportTable({
  records = [],
  onChange,
  isEditable = true
}: InternalAuditReportTableProps) {
  const categoryColors: Record<string, string> = {
    "A": "#fecaca", // Light red
    "B": "#fde68a", // Light yellow
    "C": "#6ee7b7"  // Light green
  };

  const calculateDerivedFields = (totalNC: number | null, completionNC: number | null, maxScore: number) => {
    if (totalNC === null || totalNC === undefined || completionNC === null || completionNC === undefined) {
      return { pending: null, completionPercent: null, currentScore: null };
    }
    const pending = totalNC - completionNC;
    const completionPercent = totalNC > 0 ? (completionNC / totalNC) * 100 : 0;
    const currentScore = totalNC > 0 ? (completionNC / totalNC) * maxScore : 0;
    return { pending, completionPercent, currentScore };
  };

  const handleTotalNCChange = (idx: number, newTotalNC: string) => {
    if (!onChange || !isEditable) return;

    const row = records[idx];

    // Handle N/A option - if totalNC is N/A, also set completionNC to N/A
    if (newTotalNC.toLowerCase() === 'na' || newTotalNC.toLowerCase() === 'n/a' || newTotalNC === '') {
      const { pending, completionPercent, currentScore } = calculateDerivedFields(null, null, row.maxScore);
      onChange(idx, {
        ...row,
        totalNC: null as any,
        completionNC: null as any, // Also set completionNC to N/A
        pending: pending as any,
        completionPercent: completionPercent as any,
        currentScore: currentScore as any
      });
      return;
    }

    const numTotalNC = parseFloat(newTotalNC);
    if (isNaN(numTotalNC)) return;

    const { pending, completionPercent, currentScore } = calculateDerivedFields(
      numTotalNC,
      row.completionNC,
      row.maxScore
    );

    onChange(idx, {
      ...row,
      totalNC: numTotalNC,
      pending: pending as any,
      completionPercent: completionPercent !== null ? Math.round(completionPercent * 100) / 100 : null as any,
      currentScore: currentScore !== null ? Math.round(currentScore * 1000000) / 1000000 : null as any
    });
  };

  const handleCompletionNCChange = (idx: number, newCompletionNC: string) => {
    if (!onChange || !isEditable) return;

    // Handle N/A option
    if (newCompletionNC.toLowerCase() === 'na' || newCompletionNC.toLowerCase() === 'n/a' || newCompletionNC === '') {
      const row = records[idx];
      const { pending, completionPercent, currentScore } = calculateDerivedFields(row.totalNC, null, row.maxScore);
      onChange(idx, {
        ...row,
        completionNC: null as any,
        pending: pending as any,
        completionPercent: completionPercent as any,
        currentScore: currentScore as any
      });
      return;
    }

    const numCompletionNC = parseFloat(newCompletionNC);
    if (isNaN(numCompletionNC)) return;

    const row = records[idx];
    const { pending, completionPercent, currentScore } = calculateDerivedFields(
      row.totalNC,
      numCompletionNC,
      row.maxScore
    );

    onChange(idx, {
      ...row,
      completionNC: numCompletionNC,
      pending: pending as any,
      completionPercent: completionPercent !== null ? Math.round(completionPercent * 100) / 100 : null as any,
      currentScore: currentScore !== null ? Math.round(currentScore * 1000000) / 1000000 : null as any
    });
  };

  const formatValue = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return "N/A";
    return value.toFixed(decimals);
  };

  const getDisplayValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "";
    return value.toString();
  };

  return (
    <div className="w-full overflow-x-auto mt-6">
      <div className="bg-fuchsia-500 text-white px-4 py-2 font-bold text-lg mb-2">
        INTERNAL AUDIT - % OF NON COMPLIANCE STATUS REPORT
      </div>
      <table className="min-w-full border-collapse border border-gray-300 bg-white">
        <thead>
          <tr className="bg-gray-200 border-b-2 border-gray-400">
            <th className="border border-gray-300 px-4 py-3 text-left font-bold text-sm text-gray-800">
              Recommendation Category
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              Total Number of Fire, Electrical & Life Safety Non-Conformances (NC)
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              No. of Fire, Electrical & Life Safety Non-Conformances (NC) Completion
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              No. of Pending Points
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              % of Non-Conformances Completion
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              Current Score
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              Max Score
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((row, idx) => (
            <tr
              key={idx}
              style={{ backgroundColor: categoryColors[row.category] || "#fff" }}
              className="hover:opacity-90 transition-opacity"
            >
              <td className="border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-800">
                Category {row.category}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center">
                {isEditable ? (
                  <input
                    type="text"
                    value={getDisplayValue(row.totalNC)}
                    onChange={(e) => handleTotalNCChange(idx, e.target.value)}
                    placeholder="N/A"
                    className="w-24 px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <span className="text-sm text-gray-700 font-medium">
                    {row.totalNC !== null && row.totalNC !== undefined ? row.totalNC : "N/A"}
                  </span>
                )}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center">
                {isEditable ? (
                  <input
                    type="text"
                    value={getDisplayValue(row.completionNC)}
                    onChange={(e) => handleCompletionNCChange(idx, e.target.value)}
                    placeholder="N/A"
                    disabled={row.totalNC === null || row.totalNC === undefined}
                    className={`w-24 px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${(row.totalNC === null || row.totalNC === undefined)
                        ? 'bg-gray-100 cursor-not-allowed opacity-60'
                        : ''
                      }`}
                  />
                ) : (
                  <span className="text-sm text-gray-700 font-medium">
                    {row.completionNC !== null && row.completionNC !== undefined ? row.completionNC : "N/A"}
                  </span>
                )}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                {formatValue(row.pending, 0)}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                {row.completionPercent !== null && row.completionPercent !== undefined
                  ? `${formatValue(row.completionPercent, 2)}%`
                  : "N/A"}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                {formatValue(row.currentScore, 2)}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                {row.maxScore !== null && row.maxScore !== undefined ? row.maxScore.toFixed(2) : "N/A"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
